import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldCheck, XCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Verify() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("batch") || "");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (c) => {
    const val = (c || code).trim();
    if (!val) return;
    setLoading(true); setErr(""); setResult(null);
    try {
      const { data } = await api.get(`/batch/${val}`);
      setResult(data);
    } catch (e) { setErr(e.response?.data?.detail || "Batch not found."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("batch")) lookup(params.get("batch")); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20">
      <div className="text-center">
        <ShieldCheck className="mx-auto text-ape-red mb-5" size={52} />
        <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">QUALITY ASSURANCE</span>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-2">VERIFY YOUR BATCH</h1>
        <p className="text-ape-muted mt-3">Enter the batch code printed on your vial to view its third-party certificate of analysis.</p>
      </div>

      <div className="flex gap-3 mt-10">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="e.g. BPC157-2401A" data-testid="batch-input"
          className="flex-1 bg-transparent border-2 border-ape-border rounded-none px-4 py-3 font-mono2 text-white uppercase focus:border-ape-red focus:outline-none transition-colors duration-150" />
        <button onClick={() => lookup()} disabled={loading} data-testid="batch-verify-btn"
          className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-6 flex items-center gap-2 transition-colors duration-150">
          <Search size={18} /> {loading ? "..." : "Verify"}
        </button>
      </div>

      {err && (
        <div className="mt-8 border border-ape-red bg-ape-red/10 p-6 flex items-center gap-3" data-testid="batch-error">
          <XCircle className="text-ape-red shrink-0" size={26} />
          <div><div className="font-head uppercase tracking-wide text-ape-red">Not Verified</div><p className="text-ape-muted text-sm mt-1">{err}</p></div>
        </div>
      )}

      {result && (
        <div className="mt-8 border border-ape-border bg-ape-surface" data-testid="batch-result">
          <div className="bg-green-600/20 border-b border-green-600 p-5 flex items-center gap-3">
            <CheckCircle2 className="text-green-500 shrink-0" size={30} />
            <div>
              <div className="font-display text-2xl tracking-wide text-green-400">{result.status}</div>
              <div className="font-mono2 text-xs text-ape-muted">Batch {result.batch_id} · {result.product}</div>
            </div>
          </div>
          <div className="p-6 grid sm:grid-cols-2 gap-5 font-mono2 text-sm">
            {[
              ["Product", result.product], ["Purity", result.purity], ["Test Date", result.test_date],
              ["Testing Lab", result.lab],
            ].map(([l, v]) => (
              <div key={l}><div className="text-[10px] tracking-widest text-ape-muted">{l.toUpperCase()}</div><div className="text-white mt-1">{v}</div></div>
            ))}
          </div>
          <div className="border-t border-ape-border p-6 grid sm:grid-cols-3 gap-4">
            {[["HPLC Analysis", result.hplc_pass], ["Sterility", result.sterility_pass], ["Endotoxin", result.endotoxin_pass]].map(([l, ok]) => (
              <div key={l} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="text-green-500" size={18} /><span className="font-mono2">{l}: {ok ? "PASS" : "FAIL"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
